import { useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import { getOffers, createOffer, updateOffer, toggleOffer, deleteOffer } from '../api/offers';
import { getProducts } from '../api/products';
import { getCategories } from '../api/categories';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = {
  name: '',
  description: '',
  discountType: 'PERCENTAGE',
  discountValue: '',
  startDate: '',
  endDate: '',
  productIds: [],
  categoryIds: [],
};

function CheckList({ label, items, idKey = 'id', nameKey = 'name', selected, onChange }) {
  const { t } = useTranslation();
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">{t('offers.noneAvailable')}</p>
      ) : (
        <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto divide-y divide-gray-100">
          {items.map((item) => (
            <label
              key={item[idKey]}
              className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(item[idKey])}
                onChange={() => toggle(item[idKey])}
                className="w-3.5 h-3.5 accent-blue-600"
              />
              <span className="text-sm text-gray-700">{item[nameKey]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DiscountLabel({ type, value }) {
  return type === 'PERCENTAGE'
    ? `${value}% off`
    : `৳${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })} off`;
}

function StatusBadge({ active }) {
  const { t } = useTranslation();
  return active ? (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700">{t('common.active')}</span>
  ) : (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{t('common.inactive')}</span>
  );
}

export default function Offers() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  usePageTitle('Offers');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val || undefined })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };
  const { data, isLoading } = useQuery({
    queryKey: ['offers', page, filters],
    queryFn: () => getOffers(page, 10, filters).then((r) => r.data.data),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => getProducts(0, 100).then((r) => r.data.data.content),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories-all'],
    queryFn: () => getCategories(0, 100).then((r) => r.data.data.content),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['offers'] });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      modal?.mode === 'edit' ? updateOffer(modal.data.id, payload) : createOffer(payload),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => toggleOffer(id),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteOffer(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, startDate: new Date().toISOString().split('T')[0] });
    setFormError('');
    setModal({ mode: 'create' });
  };

  const openEdit = (offer) => {
    setForm({
      name: offer.name,
      description: offer.description || '',
      discountType: offer.discountType,
      discountValue: offer.discountValue,
      startDate: offer.startDate,
      endDate: offer.endDate || '',
      productIds: offer.productIds ?? [],
      categoryIds: offer.categoryIds ?? [],
    });
    setFormError('');
    setModal({ mode: 'edit', data: offer });
  };

  const closeModal = () => setModal(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate({
      name: form.name,
      description: form.description || null,
      discountType: form.discountType,
      discountValue: parseFloat(form.discountValue),
      startDate: form.startDate,
      endDate: form.endDate || null,
      productIds: form.productIds,
      categoryIds: form.categoryIds,
    });
  };

  const offers = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('offers.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.totalElements ?? 0} {t('common.total')}</p>
        </div>
        {write && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Plus size={15} /> {t('offers.new')}
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : offers.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('offers.noOffers')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.name')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('offers.discount')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('offers.dates')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('offers.appliesTo')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.status')}</th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {offers.map((offer) => (
                <tr key={offer.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{offer.name}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <DiscountLabel type={offer.discountType} value={offer.discountValue} />
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    <span>{offer.startDate}</span>
                    <span className="mx-1 text-gray-300">→</span>
                    <span>{offer.endDate ?? '∞'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {offer.productIds?.length > 0 && (
                      <span className="mr-2">{offer.productIds.length} product{offer.productIds.length !== 1 ? 's' : ''}</span>
                    )}
                    {offer.categoryIds?.length > 0 && (
                      <span>{offer.categoryIds.length} categor{offer.categoryIds.length !== 1 ? 'ies' : 'y'}</span>
                    )}
                    {!offer.productIds?.length && !offer.categoryIds?.length && (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge active={offer.active} />
                  </td>
                  {write && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => toggleMutation.mutate(offer.id)} title={offer.active ? 'Deactivate' : 'Activate'} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          {offer.active ? <ToggleRight size={16} className="text-blue-500" /> : <ToggleLeft size={16} />}
                        </button>
                        <button onClick={() => openEdit(offer)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Pencil size={14} /></button>
                        <button onClick={() => setDeleteTarget(offer)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Create / Edit Modal */}
      {modal && (
        <Modal
          title={modal.mode === 'create' ? t('offers.new') : t('common.edit') + ' ' + t('offers.title')}
          onClose={closeModal}
          wide
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. Eid Sale"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.discount')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.discountType}
                  onChange={(e) => setForm({ ...form, discountType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="PERCENTAGE">{t('offers.percentage')}</option>
                  <option value="FLAT">{t('offers.flatAmount')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('offers.discountValue')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={form.discountValue}
                  onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={form.discountType === 'PERCENTAGE' ? '10' : '50.00'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('offers.startDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('offers.endDate')}
                  <span className="ml-1 text-xs text-gray-400 font-normal">({t('offers.endDateHint')})</span>
                </label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <CheckList
                  label={t('offers.applyToProducts')}
                  items={products}
                  idKey="id"
                  nameKey="title"
                  selected={form.productIds}
                  onChange={(ids) => setForm({ ...form, productIds: ids })}
                />
              </div>

              <div>
                <CheckList
                  label={t('offers.applyToCategories')}
                  items={categories}
                  idKey="id"
                  nameKey="name"
                  selected={form.categoryIds}
                  onChange={(ids) => setForm({ ...form, categoryIds: ids })}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder={t('common.optional')}
                />
              </div>
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <Modal title={t('offers.deleteOffer')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{deleteTarget.name}</span>? {t('common.cannotBeUndone')}
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
